import { Observable } from 'rxjs';
import { Injector, Type, ComponentFactory, ComponentRef, TemplateRef, EmbeddedViewRef } from '@angular/core';
import { NavigationExtras } from '@angular/router';

import { SgAuxRouterBridgeHostRouteChangedEvent, SgAuxRouteBridgeCreateUpdateEvent } from './events';
import { SgAuxRouter } from './aux-router.service';

export interface SgAuxRouteBridgeOutlet {
  name: string;
  /**
   * A list of path segments that prefix this outlet.
   * This should represent the parent route of this outlet.
   *
   * If the outlet is set on the root, this should be and empty array.
   *
   * ### Example:
   * ```ts
   * const routes: Routes = [
   *   {
   *      path: 'my-app',
   *      children: [
   *        { path: 'path-1', component: Component1, outlet: 'outlet1' }
   *      ]
   *   },
   * ];
   * ```
   * In the example above, the value of `relativeRoute` should be `[ 'my-app' ]
   *
   * > Note that this is a simple example, real-world scenarios are usually lazy loaded modules with names outlets.
   *
   * See https://github.com/angular/angular/issues/10981 and https://github.com/angular/angular/issues/12842 for more details.
   */
  relativeRoot: string[];
  onDestroy: Observable<void>;

  /**
   * A life-cycle hook fired before a new view is created.
   * Note the the same host can create a view multiple times (destroying pervious view each time), i.e. this will be called
   * multiple times.
   */
  auxBeforeCreateView?(): void;
  /**
   * A life-cycle hook fired before a view is destroyed.
   * Note the the same host can destroy a view multiple times, i.e. this will be called multiple times.
   */
  auxBeforeDestroyView?(): void;
}

export interface SgAuxComponentRoute<T = any> {
  path: string;
  component: Type<T>;
  injector?: Injector;
}

export interface SgAuxTemplateRoute<T = any> {
  path: string;
  template: TemplateRef<T>;
}

export type SgAuxRoute<T = any> = SgAuxComponentRoute<T> | SgAuxTemplateRoute<T>;

export interface SgAuxRouteBridgeHost {
  readonly outlet: string;
  readonly activePath: string;
  readonly activeCmpRef?: ComponentRef<any>;
  readonly activeViewRef?: EmbeddedViewRef<any>;

  onDestroy: Observable<void>;
  routeChanged: Observable<SgAuxRouterBridgeHostRouteChangedEvent>;
  createComponent<T = any>(cmpFactory: ComponentFactory<T>): ComponentRef<T>;
  createView<T = any>(tRef: TemplateRef<T>, ctx?: T): EmbeddedViewRef<T>;
  clearView(): void;
}

export interface SgAuxRouteNavigationOptions {
  params?: { [key: string]: string | number };
  navigationExtras?: NavigationExtras;
}

export interface SgAuxRouteCommandLike {
  path: string;
  outlet: string;
  options?: SgAuxRouteNavigationOptions;
}

export class SgAuxRouteCommand implements SgAuxRouteCommandLike {
  public readonly options: SgAuxRouteNavigationOptions;

  constructor(
    private auxRouter: SgAuxRouter,
    public readonly outlet: string,
    public readonly path: string,
    options?: SgAuxRouteNavigationOptions
  ) {
    this.options = options || {};
  }

  getRouteCommand(): any[] {
    return this.auxRouter.getRouteCommand(this.outlet, this.path, this.options);
  }

  /**
   * Get the URL to an aux route
   */
  getRoute(): string {
    return this.auxRouter.getRoute(this.outlet, this.path, this.options);
  }

  setRoute<T = any>(): Observable<SgAuxRouteBridgeCreateUpdateEvent<T> | false> {
    return this.auxRouter.setRoute<T>(this.outlet, this.path, this.options);
  }
}
